import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { t, useLang } from "../lib/i18n";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { TurnstileWidget } from "../components/TurnstileWidget";

export default function RegisterPage() {
  useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState(""); // YYYY-MM-DD
  const [parentEmail, setParentEmail] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  const onCaptchaToken = useCallback((tok) => setCaptchaToken(tok || ""), []);
  const onCaptchaExpire = useCallback(() => setCaptchaToken(""), []);

  // If already signed in, hop straight to the home page.
  useEffect(() => {
    if (user && typeof user === "object") {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Compute age from DOB — used to decide whether to show the parent email field.
  const age = (() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
    return a;
  })();

  const needsParentEmail = age !== null && age >= 8 && age < 14;
  const tooYoung = age !== null && age < 8;
  const captchaConfigured = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);
  const captchaReady = !captchaConfigured || Boolean(captchaToken);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (tooYoung) { setError("You need to be at least 8 to make a Reads account."); return; }
    if (needsParentEmail && !parentEmail) { setError("Please add a parent's email so we can ask for permission."); return; }
    if (!agreedToTerms) { setError("Please agree to the Terms of Service and Privacy Policy to continue."); return; }
    if (!captchaReady) { setError(t("register.captcha_required")); return; }
    setSubmitting(true);
    const result = await register(email, password, name, {
      date_of_birth: dob,
      parent_email: needsParentEmail ? parentEmail : null,
      turnstile_token: captchaToken,
    });
    setSubmitting(false);
    if (result.ok) {
      if (needsParentEmail) {
        toast.success("Account created — we've emailed your parent for permission. Ask them to click the link!");
        navigate(`/verify-email-sent?email=${encodeURIComponent(email)}`);
      } else if (result.verified === false) {
        // Normal signup — no session yet. Route to a dedicated "check
        // your email" page so the user sees the next step clearly.
        navigate(`/verify-email-sent?email=${encodeURIComponent(email)}`);
      } else {
        toast.success(t("register.verify_prompt"));
        navigate("/");
      }
    } else {
      // Bad CAPTCHA / expired token → wipe the token so the user can retry.
      setCaptchaToken("");
      setError(result.error);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-stretch fade-up pb-28 sm:pb-8">
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16 order-2 md:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">{t("register.subtitle")}</div>
            <h1 className="font-heading text-4xl text-[#0A0A0A] mt-3">{t("register.title")}</h1>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="reg-name" className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.name")}</label>
            <input id="reg-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="register-name-input" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="reg-email" className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.email")}</label>
            <input id="reg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="register-email-input" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="reg-password" className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.password")}</label>
            <input id="reg-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="register-password-input" />
            <div className="font-mono text-[10px] text-[#A3A3A3]">{t("register.min_password")}</div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="reg-dob" className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.dob")}</label>
            <input id="reg-dob" type="date" required value={dob} onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="register-dob-input" />
            {tooYoung && (
              <div className="font-mono text-[10px] text-[#B91C1C]" data-testid="register-too-young">
                {t("register.too_young")}
              </div>
            )}
            {needsParentEmail && !tooYoung && (
              <div className="font-mono text-[10px] text-[#4A4030]">{t("register.parent_help")}</div>
            )}
          </div>
          {needsParentEmail && !tooYoung && (
            <div className="space-y-1.5" data-testid="register-parent-block">
              <label htmlFor="reg-parent" className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.parent_email")}</label>
              <input id="reg-parent" type="email" required value={parentEmail} onChange={(e) => setParentEmail(e.target.value)}
                className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                data-testid="register-parent-email-input" />
            </div>
          )}

          {error && <div className="text-sm text-[#B91C1C] border border-[#B91C1C]/20 bg-[#B91C1C]/5 p-3 rounded-sm" data-testid="register-error">{error}</div>}

          <label className="flex items-start gap-3 text-[12px] text-[#4A4030] cursor-pointer select-none" data-testid="register-terms-label">
            <input
              type="checkbox"
              required
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0A0A0A] cursor-pointer flex-shrink-0"
              data-testid="register-terms-checkbox"
            />
            <span>
              I agree to the{" "}
              <Link to="/legal/condiciones" target="_blank" rel="noopener noreferrer" className="text-[#0A0A0A] underline underline-offset-2" data-testid="register-terms-link">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/legal/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#0A0A0A] underline underline-offset-2" data-testid="register-privacy-link">
                Privacy Policy
              </Link>.
            </span>
          </label>

          <div className="pt-1">
            <TurnstileWidget
              onToken={onCaptchaToken}
              onExpire={onCaptchaExpire}
              onError={onCaptchaExpire}
              testId="register-turnstile"
            />
          </div>

          <button type="submit" disabled={submitting || tooYoung || !agreedToTerms || !captchaReady}
            className="w-full bg-[#0A0A0A] hover:bg-[#262626] disabled:opacity-60 text-white font-mono uppercase tracking-widest text-xs py-3.5 rounded-sm transition-colors"
            data-testid="register-submit-button">
            {submitting ? t("register.submitting") : t("register.submit")}
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-[rgba(10,10,10,0.10)]" />
            <span className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65]">{t("auth.or")}</span>
            <div className="flex-1 h-px bg-[rgba(10,10,10,0.10)]" />
          </div>
          <GoogleSignInButton label={t("auth.signup_google")} testId="register-google" />

          <div className="text-sm text-[#525252] text-center">
            {t("register.have_account")} <Link to="/login" className="text-[#0A0A0A] underline underline-offset-4" data-testid="register-to-login">{t("nav.signin")}</Link>
          </div>
        </form>
      </div>
      <div className="hidden md:block w-1/2 bg-[#E8DFC1] order-1 md:order-2 relative overflow-hidden border-l border-[rgba(0,0,0,0.08)]">
        <div className="absolute inset-0 p-16 flex flex-col justify-between">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">reads · 2026</div>
            <h2 className="font-heading text-4xl lg:text-5xl text-[#0A0A0A] mt-8 leading-[0.98] tracking-tight">
              {t("register.side_title")}
            </h2>
            <p className="text-[#525252] mt-4 max-w-sm">{t("register.side_sub")}</p>
          </div>
          <ul className="space-y-3 font-mono text-[11px] uppercase tracking-widest text-[#525252]">
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-[#0A0A0A]" /> Free, forever</li>
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-[#0A0A0A]" /> Personalised picks</li>
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-[#0A0A0A]" /> Community reviews</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
